// Stripe payment panel — shared by airtime and (later) grocery checkout.
//
// The product is only delivered after the SERVER confirms this charge. For
// airtime that is essential: a top-up is delivered instantly and cannot be
// recovered, so a declined card after delivery would be a straight loss funded
// from our own provider balance.
//
// Card details go from the browser straight to Stripe. They never touch our
// servers or this bundle, which is what keeps PCI scope minimal.

import { useEffect, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { AlertCircle, Loader2, Lock } from 'lucide-react';
import { Button } from '../stripe-ui/Button';
import { payments, type PaymentIntentWire } from '../../lib/api';

interface Props {
  payableType: 'airtime' | 'grocery';
  payableId: number;
  /** Fires only after the SERVER has confirmed payment and fulfilled. */
  onPaid: () => void;
  onCancel?: () => void;
}

/** Stripe's loader caches per key; keep one promise per key for the session. */
const stripePromises = new Map<string, Promise<Stripe | null>>();

function stripeFor(key: string): Promise<Stripe | null> {
  let p = stripePromises.get(key);
  if (!p) {
    p = loadStripe(key);
    stripePromises.set(key, p);
  }
  return p;
}

const usd = (cents: number, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);

export function PaymentPanel({ payableType, payableId, onPaid, onCancel }: Props) {
  const [intent, setIntent] = useState<PaymentIntentWire | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    payments
      .createIntent(payableType, payableId)
      .then((i) => { if (live) setIntent(i); })
      .catch(() => { if (live) setError("We couldn't start the payment. Please try again."); });
    return () => { live = false; };
  }, [payableType, payableId]);

  if (error) {
    return (
      <p role="alert" className="text-[12.5px] text-rose-600 inline-flex items-center gap-1.5">
        <AlertCircle className="w-3.5 h-3.5" /> {error}
      </p>
    );
  }

  if (!intent) {
    return (
      <p className="py-6 text-center text-[13px] text-slate-500 inline-flex items-center gap-2 justify-center w-full">
        <Loader2 className="w-4 h-4 animate-spin" /> Preparing secure payment…
      </p>
    );
  }

  return (
    <Elements
      stripe={stripeFor(intent.publishable_key)}
      options={{
        clientSecret: intent.client_secret,
        appearance: { theme: 'stripe', variables: { colorPrimary: '#063b9e', borderRadius: '8px' } },
      }}
    >
      <PaymentForm intent={intent} onPaid={onPaid} onCancel={onCancel} />
    </Elements>
  );
}

function PaymentForm({
  intent,
  onPaid,
  onCancel,
}: {
  intent: PaymentIntentWire;
  onPaid: () => void;
  onCancel?: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements || busy) return;

    setBusy(true);
    setError(null);

    // `if_required` keeps the customer on this page for ordinary cards and only
    // redirects for methods that genuinely need it (3-D Secure, bank flows).
    const result = await stripe.confirmPayment({ elements, redirect: 'if_required' });

    if (result.error) {
      // Stripe writes these for cardholders ("Your card was declined"), so show
      // it verbatim rather than replacing it with something vaguer.
      setError(result.error.message ?? 'Payment failed. Please try another card.');
      setBusy(false);
      return;
    }

    try {
      const confirmed = await payments.confirm(intent.payment_id);

      if (confirmed.status !== 'succeeded') {
        setError('Payment did not complete. You have not been charged twice — please try again.');
        setBusy(false);
        return;
      }

      onPaid();
    } catch {
      // The charge may well have succeeded even though this call failed. The
      // Stripe webhook fulfils independently, so do NOT invite a second
      // payment — describe what is actually happening.
      setError('Payment received — confirming now. Check your activity in a moment.');
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit}>
      <PaymentElement options={{ layout: 'tabs' }} />

      {error && (
        <p role="alert" className="mt-3 text-[12.5px] text-rose-600 inline-flex items-start gap-1.5">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {error}
        </p>
      )}

      <Button type="submit" fullWidth disabled={!stripe || busy} className="mt-4">
        {busy ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Processing…</>
        ) : (
          <><Lock className="w-4 h-4" /> Pay {usd(intent.amount, intent.currency)}</>
        )}
      </Button>

      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="mt-2 w-full py-2 text-[12.5px] font-semibold text-slate-500 hover:underline disabled:opacity-50"
        >
          Back
        </button>
      )}

      <p className="mt-3 text-center text-[11.5px] text-slate-500">
        Your card is charged before the top-up is sent. If it can't be delivered,
        you're refunded automatically.
      </p>
    </form>
  );
}
